import React, { useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

import {
  RetailerNetworkFragment,
  RetailerNetworkUpdate,
  RetailerNetworkUpdateMutationOptions,
} from '@occc/graphql';
import { FullScreenProgress, snackbar } from '@occc-app/design-system';

import { useGetRetailerNetworkQuery } from '../../data-access/queries';
import { useRetailerNetworkUpdateMutation } from '../../data-access/mutations';
import { RetailerNetworkUpdateHandler } from '../retailer-connect.types';
import { RetailerNetworkErrorMessage } from '../components/retailer-network-error-message.component';
import { RetailerNetworkNoData } from '../components/retailer-network-no-data.component';

export type WithRetailerNetworkProps = {
  retailerNetwork: RetailerNetworkFragment;
  retailerNetworkUpdate: RetailerNetworkUpdateHandler;
};

export const withRetailerNetwork = <P extends Omit<object, keyof WithRetailerNetworkProps>>(
  Component: React.ComponentType<P & WithRetailerNetworkProps>,
): React.ComponentType<P> => {
  return (props: P) => {
    const isMounted = useRef(true);
    const { networkId = '' } = useParams<{ networkId: string }>();

    const { data, loading, error } = useGetRetailerNetworkQuery(
      {
        variables: { uuid: networkId },
      },
      !networkId,
    );

    const [retailerNetworkUpdateMutation] = useRetailerNetworkUpdateMutation({
      onCompleted: () => {
        snackbar.success('Retailer Network has been successfully updated');
      },
    });

    const retailerNetworkUpdate = useCallback(
      async (
        input: Omit<RetailerNetworkUpdate, 'uuid'>,
        refetchQueries: RetailerNetworkUpdateMutationOptions['refetchQueries'],
      ) => {
        if (!data?.retailerNetwork) {
          return;
        }
        await retailerNetworkUpdateMutation({
          refetchQueries,
          variables: {
            input: {
              uuid: data.retailerNetwork.uuid,
              ...input,
            },
          },
        });
      },
      [data?.retailerNetwork, retailerNetworkUpdateMutation],
    );

    useEffect(() => {
      isMounted.current = true;
      return () => {
        isMounted.current = false;
      };
    }, []);

    if (!isMounted.current) {
      return null;
    }

    if (loading && !data) {
      return <FullScreenProgress />;
    }

    if (error) {
      return <RetailerNetworkErrorMessage />;
    }

    if (!data) {
      return <RetailerNetworkNoData />;
    }

    return (
      <Component
        {...props}
        retailerNetwork={data.retailerNetwork}
        retailerNetworkUpdate={retailerNetworkUpdate}
      />
    );
  };
};
